import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnchor, faUsers, faBullseye, faEye, faGem, faRocket, faWater, faShareAlt, faEnvelope, faShip, faUserShield, faStar } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faYoutube, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import '../styles/about/AboutUsPage.css';
import SEOHead from '../components/Common/SEOHead';
import WebPreloader from '../components/Common/WebPreloader';
import bannerImage from '../../images/AboutUsPage/TravelWebKitBase_AboutUsBanner.png';
import tripAdvisorIcon from '../../images/HomePage/MrSea2025_TripAdvisorIcon.svg';
import CustomBoatLeadModal from '../components/HomePage/CustomBoatLeadModal';
import FAQ from '../components/HomePage/FAQ';

const AboutUsPage = () => {
  const [showPreloader, setShowPreloader] = useState(true);
  const [activeSection, setActiveSection] = useState('hero');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowPreloader(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    const sections = document.querySelectorAll('.about-section, .about-hero__title, .about-hero__subtitle');
    sections.forEach((section) => {
      observer.observe(section);
    });

    const handleScroll = () => {
        const sectionsForNav = [
            { id: 'hero' }, { id: 'story' }, { id: 'core' }, { id: 'social' }, { id: 'cta' }
        ];
        
        const scrollPosition = window.scrollY + window.innerHeight * 0.5;

        let currentActive = 'hero';
        for (const section of sectionsForNav) {
            const element = document.getElementById(section.id);
            if (element && element.offsetTop <= scrollPosition) {
                currentActive = section.id;
            }
        }
        setActiveSection(currentActive);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      sections.forEach((section) => {
        if (section) observer.unobserve(section);
      });
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navItems = [
    { id: 'hero', icon: faWater, label: 'Top' },
    { id: 'story', icon: faAnchor, label: 'Story' },
    { id: 'core', icon: faGem, label: 'Values' },
    { id: 'social', icon: faShareAlt, label: 'Connect' },
    { id: 'cta', icon: faRocket, label: 'Book Now' }
  ];


  return (
    <div className="about-page-immersive">
      <SEOHead pageType="about" />
      {showPreloader && <WebPreloader />}

      <nav className="about-side-nav">
        <ul>
          {navItems.map(item => (
            <li key={item.id} className={activeSection === item.id ? 'active' : ''}>
              <button onClick={() => scrollToSection(item.id)} title={item.label}>
                <FontAwesomeIcon icon={item.icon} />
                <span className="nav-label">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="about-hero">
        <div className="about-hero__bg-image" style={{ backgroundImage: `url(${bannerImage})` }}></div>
        <div className="about-hero__overlay"></div>
        <div className="about-hero__content">
            <h1 className="about-hero__title">About Mr. Sea</h1>
            <p className="about-hero__subtitle">Miami's Premier Boat Rental Company</p>
        </div>
        <div className="about-hero__wave-container">
            <svg className="waves" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
              viewBox="0 24 150 28" preserveAspectRatio="none" shapeRendering="auto">
              <defs>
                <path id="gentle-wave" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
              </defs>
              <g className="parallax">
                <use xlinkHref="#gentle-wave" x="48" y="0" fill="rgba(0, 96, 152, 0.2)" />
                <use xlinkHref="#gentle-wave" x="48" y="3" fill="rgba(0, 96, 152, 0.4)" />
                <use xlinkHref="#gentle-wave" x="48" y="5" fill="rgba(0, 96, 152, 0.6)" />
                <use xlinkHref="#gentle-wave" x="48" y="7" fill="#006098" />
              </g>
            </svg>
        </div>
      </section>
      
      <main className="about-content">
        {/* Story & Team Section */}
        <section id="story" className="about-section about-story-team">
            <div className="about-container">
                <div className="story-team-grid">
                    <div className="story-content">
                        <FontAwesomeIcon icon={faAnchor} className="about-section__bg-icon" />
                        <h2>Our Story</h2>
                        <p>
                            Hello, I'm <strong>Leo Galarza</strong>, owner of Mr. Sea Miami Boat Rentals. 
                            I established the company in 2018 to provide a <strong>higher level of professionalism 
                            and customer service</strong> to visitors seeking a Miami on-the-water experience.
                        </p>
                         <p>
                            I hope you will contact us for your boat rental so <strong>we can welcome you to Miami 
                            with our trademark hospitality and give you an awesome experience you'll never forget!</strong>
                        </p>
                    </div>
                    <div className="team-content">
                         <FontAwesomeIcon icon={faUsers} className="about-section__bg-icon" />
                        <h2>Our Team</h2>
                        <p>
                            At Mr. Sea Miami, we pride ourselves on having a team of <strong>certified captains </strong> 
                            and experienced crew members who are passionate about providing exceptional service. 
                            Our team is dedicated to ensuring your safety and comfort while creating unforgettable 
                            memories on the water.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        {/* Mission, Vision & Values Section */}
        <section id="core" className="about-section about-core">
             <div className="about-container">
                <h2>Our Core Principles</h2>
                <div className="about-core__grid">
                    <div className="about-core__item">
                        <div className="about-core__icon"><FontAwesomeIcon icon={faBullseye} /></div>
                        <h3>Our Mission</h3>
                        <p>To provide a superior on-the-water experience through professionalism, a wide variety of clean and well-maintained boats, and exceptional customer service.</p>
                    </div>
                    <div className="about-core__item">
                        <div className="about-core__icon"><FontAwesomeIcon icon={faEye} /></div>
                        <h3>Our Vision</h3>
                        <p>To be the most trusted and sought-after boat rental company in South Florida, known for creating a passionate, unforgettable service at sea.</p>
                    </div>
                     <div className="about-core__item">
                        <div className="about-core__icon"><FontAwesomeIcon icon={faGem} /></div>
                        <h3>Our Values</h3>
                        <p>Safety First, Integrity, Excellence, and Passion. These are the pillars that guide every tour and interaction we have with our valued guests.</p>
                    </div>
                </div>
            </div>
        </section>

        {/* Social Media Section */}
        <section id="social" className="about-section about-social">
            <div className="about-container">
                <h2>Join Our Voyage</h2>
                <p>Follow our journey and stay updated with our latest offers and sun-soaked adventures on the Miami waters.</p>
                <div className="social-links">
                    <a href="https://www.instagram.com/mrseamiami/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                        <FontAwesomeIcon icon={faInstagram} />
                        <span>Instagram</span>
                    </a>
                    <a href="https://www.linkedin.com/company/mr-sea-boat-rentals-miami/posts/?feedView=all" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                        <FontAwesomeIcon icon={faLinkedin} />
                        <span>LinkedIn</span>
                    </a>
                    <a href="https://www.youtube.com/@mr.seamiami3153" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                        <FontAwesomeIcon icon={faYoutube} />
                        <span>YouTube</span>
                    </a>
                    <a href="mailto:hello@mrseamiami.com" aria-label="Email">
                        <FontAwesomeIcon icon={faEnvelope} />
                         <span>Email Us</span>
                    </a>
                </div>
            </div>
        </section>

        {/* CTA Section */}
        <section id="cta" className="about-section about-cta">
            <div className="about-container">
                <div className="cta-content">
                    <h2 className="cta-title">Ready for Your Unforgettable Miami Experience?</h2>
                    <p className="cta-subtitle">The sea is calling. Let's create your perfect day on the water.</p>
                    <button className="cta-button" onClick={() => setIsModalOpen(true)}>Book Your Adventure Now</button>
                    <div className="cta-features">
                        <div className="cta-feature-item">
                            <FontAwesomeIcon icon={faShip} />
                            <span>60+ Boats Available</span>
                        </div>
                        <div className="cta-feature-item">
                            <img src={tripAdvisorIcon} alt="TripAdvisor Reviews" className="cta-tripadvisor-icon" />
                            <span>Over 100 5-Star Reviews</span>
                        </div>
                        <div className="cta-feature-item">
                            <FontAwesomeIcon icon={faStar} />
                            <span>5000+ Happy Guests</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
      </main>
      
      <FAQ />
      
      {isModalOpen && <CustomBoatLeadModal onClose={() => setIsModalOpen(false)} />}
    </div>
  );
};

export default AboutUsPage;
